import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const ContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState<string>();
  const { options: empOptions, empLabel } = useEmployeeOptions();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.contracts(employeeId),
    queryFn: () => hrmsService.listContracts(employeeId ? { employee_id: employeeId } : undefined),
  });

  const stats = useMemo(() => {
    const draft = rows.filter((r: any) => r.status === 'Draft').length;
    const active = rows.filter((r: any) => r.status === 'Active' || r.status === 'Approved').length;
    return { total: rows.length, draft, active };
  }, [rows]);

  const columns: ColumnsType<any> = [
    {
      title: 'Contract #',
      dataIndex: 'contract_number',
      width: 140,
      fixed: 'left',
      render: (v: string, r) => <a onClick={() => navigate(`/hr/contracts/${r.id}`)}>{v || 'Open'}</a>,
    },
    { title: 'Ver', dataIndex: 'version_number', width: 60 },
    { title: 'Type', dataIndex: 'contract_type', width: 100 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'From',
      dataIndex: 'effective_from',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'To',
      dataIndex: 'effective_to',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    { title: 'Basic', dataIndex: 'basic_salary', width: 110 },
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/hr/contracts/${r.id}`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Employment contracts"
          subtitle="Versioned employment contracts"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/contracts/new')}>
              New contract
            </Button>
          }
        />
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Total" value={stats.total} /></Card></Col>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Draft" value={stats.draft} /></Card></Col>
        <Col xs={24} sm={8}><Card size="small"><Statistic title="Active" value={stats.active} /></Card></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Select
            allowClear
            placeholder="Filter employee"
            style={{ width: 280 }}
            options={empOptions}
            value={employeeId}
            onChange={setEmployeeId}
            showSearch
            optionFilterProp="label"
          />
          <Text type="secondary">{rows.length} record(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20 }}
        onRow={(r) => ({ onClick: () => navigate(`/hr/contracts/${r.id}`), style: { cursor: 'pointer' } })}
      />
    </WorkspaceLayout>
  );
};

export default ContractsPage;
